<template>
  <div class="solution-content">
    <div class="toolbar">
      <el-input
        v-model="searchQuery"
        placeholder="搜索解决方案标题..."
        style="width: 300px"
        @input="onSearchInput"
        clearable
      >
        <template #prefix>
          <el-icon><Search /></el-icon>
        </template>
      </el-input>
      <el-button type="primary" @click="showAddModal">新增解决方案</el-button>
    </div>

    <el-table :data="list" style="width: 100%" border>
      <el-table-column prop="id" label="ID" width="80"></el-table-column>
      <el-table-column prop="title" label="解决方案标题"></el-table-column>
      <el-table-column prop="cover_photo_path" label="封面图片" width="100">
        <template #default="scope">
          <el-image
            v-if="scope.row.cover_photo_path"
            :src="storageSrc(scope.row.cover_photo_path)"
            style="width: 50px; height: 50px"
            fit="cover"
          ></el-image>
          <span v-else>暂无图片</span>
        </template>
      </el-table-column>
      <el-table-column prop="summary" label="摘要" show-overflow-tooltip></el-table-column>
      <el-table-column prop="author" label="作者" width="100"></el-table-column>
      <el-table-column prop="date" label="日期" width="120"></el-table-column>
      <el-table-column prop="body" label="富文本内容" min-width="160" show-overflow-tooltip></el-table-column>
      <el-table-column label="操作" width="180" fixed="right">
        <template #default="scope">
          <el-button type="primary" size="small" @click="editItem(scope.row)">编辑</el-button>
          <el-button type="danger" size="small" @click="deleteItem(scope.row.id)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <div class="pager-wrap">
      <el-pagination
        :current-page="page"
        :page-size="pageSize"
        :page-sizes="[10, 20, 50, 100]"
        layout="total, sizes, prev, pager, next, jumper"
        :total="total"
        background
        @current-change="onPageChange"
        @size-change="onPageSizeChange"
      />
    </div>

    <el-dialog
      v-model="showModal"
      :title="isEditing ? '编辑解决方案' : '新增解决方案'"
      width="700px"
      destroy-on-close
      @opened="onDialogOpened"
      @closed="onDialogClosed"
    >
      <el-form :model="formData" :rules="rules" ref="formRef" label-width="100px">
        <el-form-item label="解决方案标题" prop="title">
          <el-input v-model="formData.title" placeholder="请输入解决方案标题"></el-input>
        </el-form-item>
        <el-form-item label="封面图片" prop="cover_photo_path">
          <el-upload
            class="upload-demo"
            action=""
            accept="image/*"
            :show-file-list="false"
            :http-request="handleCoverHttpRequest"
            :before-upload="beforeCoverUpload"
            :limit="1"
            list-type="picture-card"
            :disabled="uploading"
          >
            <el-icon v-if="!formData.cover_photo_path && !uploading" class="upload-icon"><Plus /></el-icon>
            <el-icon v-if="uploading" class="upload-icon is-loading"><Loading /></el-icon>
            <el-image v-else-if="formData.cover_photo_path" :src="storageSrc(formData.cover_photo_path)" class="upload-image" fit="cover"></el-image>
          </el-upload>
        </el-form-item>
        <el-form-item label="摘要" prop="summary">
          <el-input v-model="formData.summary" type="textarea" :rows="3" placeholder="请输入摘要"></el-input>
        </el-form-item>
        <el-form-item label="作者" prop="author">
          <el-input v-model="formData.author" placeholder="请输入作者"></el-input>
        </el-form-item>
        <el-form-item label="日期" prop="date">
          <el-date-picker
            v-model="formData.date"
            type="date"
            placeholder="选择日期"
            style="width: 100%"
            value-format="YYYY-MM-DD"
          ></el-date-picker>
        </el-form-item>
        <el-form-item label="富文本内容" prop="body" class="form-item-rich">
          <div v-if="editorMountReady" class="rich-editor-wrap">
            <Toolbar
              v-if="editorRef"
              class="rich-toolbar"
              :editor="editorRef"
              :defaultConfig="toolbarConfig"
              mode="default"
            />
            <Editor
              class="rich-editor"
              v-model="formData.body"
              :defaultConfig="editorConfig"
              mode="default"
              @onCreated="handleCreated"
            />
          </div>
          <div v-else class="rich-editor-skeleton">编辑器加载中…</div>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="closeModal">取消</el-button>
        <el-button type="primary" @click="handleSubmit" :loading="submitting">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { Search, Plus, Loading } from '@element-plus/icons-vue'
import { getSolutionList, addSolution, updateSolution, deleteSolution, uploadFile } from '@/api/index'
import {
  resolveUploadedImageUrl as buildUploadImageUrl,
  getUploadRelativePath,
  getStorageFileUrl,
  absolutizeStorageUrlsInHtml,
  compressImageIfNeeded
} from '@/utils/upload'
import { parseListAndPagination } from '@/utils/pagination'
import '@wangeditor/editor/dist/css/style.css'
import { Editor, Toolbar } from '@wangeditor/editor-for-vue'
import { markRaw } from 'vue'

function createEmptySolutionForm() {
  return {
    id: null,
    title: '',
    cover_photo_path: '',
    summary: '',
    author: '',
    date: '',
    body: ''
  }
}

export default {
  name: 'SolutionPage',
  components: {
    Search,
    Plus,
    Loading,
    Editor,
    Toolbar
  },
  data() {
    return {
      searchQuery: '',
      showModal: false,
      editorMountReady: false,
      isEditing: false,
      list: [],
      total: 0,
      page: 1,
      pageSize: 10,
      searchDebounceTimer: null,
      uploading: false,
      submitting: false,
      editorRef: null,
      toolbarConfig: {},
      formData: createEmptySolutionForm(),
      rules: {
        title: [{ required: true, message: '请输入解决方案标题', trigger: 'blur' }]
      }
    }
  },
  beforeUnmount() {
    this.destroyEditorIfAny()
  },
  created() {
    this.loadData()
  },
  computed: {
    editorConfig() {
      return {
        placeholder: '请输入富文本内容...',
        MENU_CONF: {
          uploadImage: {
            maxFileSize: 5 * 1024 * 1024,
            maxNumberOfFiles: 10,
            allowedFileTypes: ['image/*'],
            customUpload: (file, insertFn) => this.editorImageCustomUpload(file, insertFn)
          }
        }
      }
    }
  },
  methods: {
    parseApiListPayload(res) {
      const d = res?.data
      if (!d) return []
      if (Array.isArray(d)) return d
      if (Array.isArray(d.list)) return d.list
      if (Array.isArray(d.data)) return d.data
      if (Array.isArray(d.items)) return d.items
      return []
    },
    normalizeSolutionRow(row) {
      const empty = createEmptySolutionForm()
      if (!row || typeof row !== 'object') return { ...empty }
      const pick = (...keys) => {
        for (const k of keys) {
          const v = row[k]
          if (v !== undefined && v !== null && v !== '') return v
        }
        return undefined
      }
      const str = (v, fallback = '') => (v != null && v !== '' ? String(v) : fallback)
      const toYmd = (v) => {
        if (v == null || v === '') return ''
        const s = String(v)
        const m = s.match(/(\d{4}-\d{2}-\d{2})/)
        return m ? m[1] : s.slice(0, 10)
      }

      const titleV = pick('title', 'name', 'solution_title')
      const coverV = pick('cover_photo_path', 'cover_image', 'cover', 'image', 'thumb')
      const summaryV = pick('summary', 'description', 'desc', 'abstract')
      const authorV = pick('author', 'writer', 'creator_name')
      const dateV = pick('date', 'published_at', 'publish_date', 'created_at')
      const bodyV = pick('body', 'content', 'detail', 'html', 'rich_text')

      return {
        id: row.id != null ? row.id : empty.id,
        title: str(titleV, str(row.title, empty.title)),
        cover_photo_path: str(coverV, str(row.cover_photo_path, empty.cover_photo_path)),
        summary: str(summaryV, str(row.summary, empty.summary)),
        author: str(authorV, str(row.author, empty.author)),
        date: dateV != null ? toYmd(dateV) : toYmd(row.date) || empty.date,
        body: str(bodyV, str(row.body, empty.body))
      }
    },
    storageSrc(path) {
      return getStorageFileUrl(path)
    },
    async editorImageCustomUpload(file, insertFn) {
      try {
        if (file.size > 2 * 1024 * 1024) {
          this.$message.info('图片较大，正在压缩…')
        }
        const fileToUpload = await compressImageIfNeeded(file)
        const formData = new FormData()
        formData.append('file', fileToUpload)
        const res = await uploadFile(formData)
        const imageUrl = buildUploadImageUrl(res)
        if (!imageUrl) throw new Error('服务器未返回图片地址')
        insertFn(imageUrl, '图片', imageUrl)
      } catch (e) {
        console.error('富文本图片上传失败:', e)
        this.$message.error(e.message || '图片上传失败')
      }
    },
    onDialogOpened() {
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          this.editorMountReady = true
        })
      })
    },
    onDialogClosed() {
      this.editorMountReady = false
      this.editorRef = null
    },
    destroyEditorIfAny() {
      const editor = this.editorRef
      if (editor == null) return
      try {
        editor.destroy()
      } catch (e) {
        console.debug('editor.destroy', e)
      }
      this.editorRef = null
    },
    handleCreated(editor) {
      this.editorRef = markRaw(editor)
      this.$nextTick(() => {
        editor.focus?.()
      })
    },
    async loadData() {
      try {
        const params = { page: this.page, per_page: this.pageSize }
        const q = (this.searchQuery || '').trim()
        if (q) params.title = q
        const res = await getSolutionList(params)
        const { rows, total } = parseListAndPagination(res)
        let mapped = rows.map((row) => this.normalizeSolutionRow(row))
        if (mapped.length === 0 && total > 0 && this.page > 1) {
          this.page -= 1
          return await this.loadData()
        }
        this.list = mapped
        this.total = total
      } catch (error) {
        console.error('加载数据失败:', error)
        this.$message.error('加载数据失败')
      }
    },
    onSearchInput() {
      clearTimeout(this.searchDebounceTimer)
      this.searchDebounceTimer = setTimeout(() => {
        this.page = 1
        this.loadData()
      }, 400)
    },
    onPageChange(p) {
      if (p == null) return
      this.page = p
      this.loadData()
    },
    onPageSizeChange(size) {
      this.pageSize = size
      this.page = 1
      this.loadData()
    },
    showAddModal() {
      this.isEditing = false
      this.formData = createEmptySolutionForm()
      this.showModal = true
    },
    editItem(row) {
      this.isEditing = true
      this.formData = this.normalizeSolutionRow(row)
      this.showModal = true
    },
    async deleteItem(id) {
      this.$confirm('确定要删除这条记录吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      })
        .then(async () => {
          try {
            await deleteSolution(id)
            await this.loadData()
            this.$message({
              type: 'success',
              message: '删除成功!'
            })
          } catch (error) {
            console.error('删除失败:', error)
            this.$message.error('删除失败!')
          }
        })
        .catch(() => {
          this.$message({
            type: 'info',
            message: '已取消删除'
          })
        })
    },
    beforeCoverUpload(file) {
      const raw = file.raw != null ? file.raw : file
      if (!raw.type || !String(raw.type).includes('image')) {
        this.$message.error('只能上传图片文件!')
        return false
      }
      return true
    },
    async handleCoverHttpRequest(options) {
      const rawFile = options.file.raw != null ? options.file.raw : options.file
      this.uploading = true
      try {
        if (rawFile.size > 2 * 1024 * 1024) {
          this.$message.info('图片较大，正在压缩…')
        }
        const fileToUpload = await compressImageIfNeeded(rawFile)
        const formData = new FormData()
        formData.append('file', fileToUpload)
        const res = await uploadFile(formData)
        const relPath = getUploadRelativePath(res)
        if (!relPath) throw new Error('服务器未返回图片路径')
        this.formData.cover_photo_path = relPath
        if (typeof options.onSuccess === 'function') options.onSuccess(res)
        this.$message.success('图片上传成功!')
      } catch (error) {
        console.error('上传失败:', error)
        if (typeof options.onError === 'function') options.onError(error)
        this.$message.error('图片上传失败!')
      } finally {
        this.uploading = false
      }
    },
    async handleSubmit() {
      this.$refs.formRef.validate(async (valid) => {
        if (!valid) return
        this.submitting = true
        try {
          const payload = {
            ...this.formData,
            body: absolutizeStorageUrlsInHtml(this.formData.body || '')
          }
          if (this.isEditing) {
            await updateSolution(payload)
            this.$message.success('编辑成功!')
          } else {
            await addSolution(payload)
            this.$message.success('新增成功!')
          }
          await this.loadData()
          this.closeModal()
        } catch (error) {
          console.error('操作失败:', error)
          this.$message.error(this.isEditing ? '编辑失败!' : '新增失败!')
        } finally {
          this.submitting = false
        }
      })
    },
    closeModal() {
      this.showModal = false
      this.formData = createEmptySolutionForm()
    }
  }
}
</script>

<style scoped>
.solution-content {
  height: 100%;
}

.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
  gap: 10px;
}

.pager-wrap {
  margin-top: 12px;
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
}

.upload-demo {
  text-align: center;
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: border-color 0.3s;
}

.upload-demo:hover {
  border-color: #409eff;
}

.upload-icon {
  font-size: 28px;
  color: #8c939d;
  width: 148px;
  height: 148px;
  line-height: 148px;
  text-align: center;
}

.upload-icon.is-loading {
  animation: rotating 2s linear infinite;
}

@keyframes rotating {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.upload-image {
  width: 148px;
  height: 148px;
  display: block;
}

.form-item-rich :deep(.el-form-item__content) {
  flex: 1;
  min-width: 0;
  max-width: 100%;
}

.rich-editor-wrap {
  width: 100%;
  max-width: 100%;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-sizing: border-box;
  overflow: hidden;
}

.rich-toolbar {
  border-bottom: 1px solid #ccc;
}

.rich-toolbar :deep(.w-e-toolbar) {
  width: 100% !important;
}

.rich-editor {
  height: 400px;
  overflow-y: hidden;
}

.rich-editor :deep(.w-e-text-container),
.rich-editor :deep(.w-e-scroll) {
  width: 100% !important;
}

.rich-editor :deep(.w-e-text-container) {
  min-height: 360px;
}

.rich-editor-skeleton {
  width: 100%;
  height: 400px;
  line-height: 400px;
  text-align: center;
  color: #909399;
  border: 1px dashed #dcdfe6;
  border-radius: 4px;
  background: #fafafa;
  box-sizing: border-box;
}
</style>
